import { Box, Typography, useTheme } from '@mui/material'
import { Security } from '../../../../proto/generated/security'
import React, { memo, useContext, useMemo } from 'react'
import { LineData, Time } from 'lightweight-charts'
import './seriesMetadataDisplay.css'
import { AppContext } from '../../../../App'
import { getSecurityKey } from '../../../../util/security'
import { findNearestIndex } from './seriesOptionMetadataDisplay'
import TimeBuckets from '../../../timeBuckets/TimeBuckets'
import { ChartViewKey } from '../../../../assets/constants'
import MultiStrikeChartTypeMenu from '../../../../pages/home/openInterestHeader/MultiStrikeChartTypeMenu'

export type SeriesMetadataProps = {
    seriesData: LineData<Time>
    security: Security
    hoverdTime: number
    viewKey: string
}

const SeriesMetadataDisplay: React.FC<SeriesMetadataProps> = ({ seriesData, security, hoverdTime, viewKey }) => {
    const theme = useTheme()
    const appContext = useContext(AppContext)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const secKey = useMemo(() => {
        if (security?.exchange && security?.code) {
            return getSecurityKey(security.exchange, security.code)
        }
        return undefined
    }, [security?.exchange, security?.code])
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])

    const value: number | undefined = useMemo(() => {
        if (seriesData) return seriesData.value
        if (chart && secKey) {
            const data: any = chart.dataManager.seriesApis?.[secKey]?.data
            if (data) {
                let index = findNearestIndex(data, hoverdTime)
                let value = undefined
                while (value === undefined && index >= 0) {
                    value = data[index]?.value
                    index = index - 1
                }
                return value
            }
        }
        return undefined
    }, [chart, hoverdTime, secKey, seriesData])

    return <Typography
        color={'textSecondary'}
        component={'div'}
        className="series-name flex items-center"
        sx={{
            borderColor: 'transparent',
            width: 'fit-content',
            fontSize: '14px',
            height: '24px',
            pl: '4px',
            border: `1px solid ${theme.palette.background.paper}`,
            borderRadius: '4px',
            '&:hover': {
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxSizing: 'border-box'
            },
            '&:hover .setting-btn': {
                visibility: 'visible !important'
            }
        }}
    >
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            fontWeight: 500,
            textWrap: 'nowrap'
        }} className="font-feature-num">
            {security?.displayName} {
            isWidget ?
                <>
                    <MultiStrikeChartTypeMenu viewKey={viewKey} isWidget={isWidget} compact />
                    <TimeBuckets registerShortcut={false} viewKey={viewKey} key={viewKey} showFavorites={false} buttonProps={{
                        sx: { margin: 0, padding: 0 }
                    }}/>
                </> :
                null
        } 
        <Typography fontSize={'13px'}>{value?.toFixed(2)}</Typography>
        </Box>
    </Typography>
}
export default memo(SeriesMetadataDisplay)
